import React, { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import WeekPicker, { getToday, getWeekStart, getWeekEnd } from "../shared/WeekPicker";

interface WeeklyInsightsHeaderProps {
  onWeekChange?: (weekStart: Date, weekEnd: Date) => void;
  minDate?: Date;
}

const WeeklyInsightsHeader: React.FC<WeeklyInsightsHeaderProps> = ({ onWeekChange, minDate }) => {
  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(getToday()));
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleWeekSelect = (ws: Date) => {
    setWeekStart(ws);
    onWeekChange?.(ws, getWeekEnd(ws));
  };

  const shiftWeek = (delta: number) => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + delta * 7);
    if (delta > 0 && next > getWeekStart(getToday())) return;
    handleWeekSelect(next);
  };

  const we = getWeekEnd(weekStart);
  const canGoNext = getWeekEnd(weekStart) < getToday();
  const canGoPrev = !minDate || weekStart > minDate;

  const formatRange = (s: Date, e: Date) => {
    const o: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
    return `${s.toLocaleDateString("en-GB", o)} - ${e.toLocaleDateString("en-GB", o)}`;
  };

  return (
    <div ref={ref} className="relative flex items-center gap-1">
      <button
        onClick={() => canGoPrev && shiftWeek(-1)}
        disabled={!canGoPrev}
        className={`p-1 rounded transition ${!canGoPrev ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"}`}
      >
        <ChevronLeft className="w-4 h-4 text-gray-500" />
      </button>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#E5E7EB] bg-white hover:bg-gray-50 transition-colors"
      >
        <span className="text-[13px] font-medium text-[#1F1F1F] font-inter whitespace-nowrap">
          {formatRange(weekStart, we)}
        </span>
        <Calendar className="w-4 h-4 text-[#00A9A9]" />
      </button>
      <button
        onClick={() => canGoNext && shiftWeek(1)}
        disabled={!canGoNext}
        className={`p-1 rounded transition ${!canGoNext ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"}`}
      >
        <ChevronRight className="w-4 h-4 text-gray-500" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-2 z-50">
          <WeekPicker
            selectedWeekStart={weekStart}
            onWeekSelect={handleWeekSelect}
            onClose={() => setOpen(false)}
            minDate={minDate}
          />
        </div>
      )}
    </div>
  );
};

export default WeeklyInsightsHeader;
export { getWeekStart, getWeekEnd };
